import { Injectable, inject } from '@angular/core';

import { TaskModel } from '@core/models/task.model';
import * as fromRoot from '@core/redux/index';
import { BadgeService } from '@core/services/badge.service';
import { TaskService } from '@core/services/task.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, filter, withLatestFrom } from 'rxjs/operators';

import {
  CreateAction,
  CreateFailureAction,
  CreateSuccessAction,
  TaskActionTypes,
  SaveAction,
  SaveFailureAction,
  SaveSuccessAction,
  SearchFailureAction,
  SearchSuccessAction,
  OrderAction,
  OrderSuccessAction,
  OrderFailureAction,
  FavoriteAction,
  FavoriteSuccessAction,
  FavoriteFailureAction,
  SearchFavoriteSuccessAction,
  SearchFavoriteFailureAction,
  FindOneAction,
  FindOneSuccessAction,
  FindOneFailureAction,
  SearchNotificationSuccessAction,
  SearchNotificationFailureAction,
  SearchAction,
  ParticleAction,
  ParticleSuccessAction,
  ParticleFailureAction,
  ArchiveAction,
  ArchiveSuccessAction,
  ArchiveFailureAction,
  OpenAction,
  MoveToProjectAction,
  MoveToProjectSuccessAction,
  MoveToProjectFailureAction,
} from './task.actions';

@Injectable()
export class TaskEffects {
  private store = inject<Store<fromRoot.State>>(Store);
  private actions$ = inject(Actions);
  private taskService = inject(TaskService);
  private badgeService = inject(BadgeService);

  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.SearchAction),
      map((action: SearchAction) => action.payload),
      switchMap((query: any) =>
        this.taskService.findAll(query).pipe(
          map(task => new SearchSuccessAction(task)),
          catchError(errors => of(new SearchFailureAction(errors)))
        )
      )
    )
  );

  searchParticle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.ParticleAction),
      map((action: ParticleAction) => action.payload),
      switchMap((query: any) =>
        this.taskService.findAll(query).pipe(
          map(task => new ParticleSuccessAction(task)),
          catchError(errors => of(new ParticleFailureAction(errors)))
        )
      )
    )
  );

  searchNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.SearchNotificationAction),
      switchMap(() =>
        this.taskService.findAllNotification().pipe(
          map(task => new SearchNotificationSuccessAction(task)),
          catchError(errors => of(new SearchNotificationFailureAction(errors)))
        )
      )
    )
  );

  findOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.FindOneAction),
      map((action: FindOneAction) => action.payload),
      switchMap((id: string) =>
        this.taskService.findOne(id).pipe(
          map(task => new FindOneSuccessAction(task)),
          catchError(errors => of(new FindOneFailureAction(errors)))
        )
      )
    )
  );

  searchFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.SearchFavoriteAction),
      switchMap(() =>
        this.taskService.findAllFavorite().pipe(
          map(task => new SearchFavoriteSuccessAction(task)),
          catchError(errors => of(new SearchFavoriteFailureAction(errors)))
        )
      )
    )
  );

  order$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.OrderAction),
      map((action: OrderAction) => action.payload),
      switchMap((model: TaskModel) =>
        this.taskService.order(model).pipe(
          map(task => new OrderSuccessAction(task)),
          catchError(errors => of(new OrderFailureAction(errors)))
        )
      )
    )
  );

  archive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.ArchiveAction),
      map((action: ArchiveAction) => action.payload),
      switchMap((model: TaskModel) =>
        this.taskService.save(model).pipe(
          map(task => new ArchiveSuccessAction(task)),
          catchError(errors => of(new ArchiveFailureAction(errors)))
        )
      )
    )
  );

  favorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.FavoriteAction),
      map((action: FavoriteAction) => action.payload),
      switchMap((model: TaskModel) =>
        this.taskService.favorite(model).pipe(
          map(task => new FavoriteSuccessAction(task)),
          catchError(errors => of(new FavoriteFailureAction(errors)))
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.CreateAction),
      map((action: CreateAction) => action.payload),
      switchMap((model: TaskModel) =>
        this.taskService.create(model).pipe(
          switchMap(task => [
            // new CloseTaskFormMenu(),
            new CreateSuccessAction(task),
          ]),
          catchError(errors => of(new CreateFailureAction(errors)))
        )
      )
    )
  );

  save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.SaveAction),
      map((action: SaveAction) => action.payload),
      switchMap((model: TaskModel) =>
        this.taskService.save(model).pipe(
          map(task => new SaveSuccessAction(task)),
          catchError(errors => of(new SaveFailureAction(errors)))
        )
      )
    )
  );

  openTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.OpenAction),
      map((action: OpenAction) => action.payload),
      filter(id => !!id),
      switchMap((id: string) =>
        this.taskService.findOne(id).pipe(
          map(task => new FindOneSuccessAction(task)),
          catchError(errors => {
            return of(new FindOneFailureAction(errors));
          })
        )
      )
    )
  );

  updateBadge$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TaskActionTypes.UpdateNotificationAction,
          TaskActionTypes.SearchNotificationSuccessAction,
          TaskActionTypes.ClearNotificationsAction
        ),
        withLatestFrom(this.store.select(fromRoot.getNotificationsTask)),
        map(([_, notifications]) => {
          const totalCount = notifications.reduce((sum, task) => sum + (task.countNotifications || 0), 0);
          this.badgeService.setBadgeCount(totalCount);
        })
      ),
    { dispatch: false }
  );

  moveToProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActionTypes.MoveToProjectAction),
      map((action: MoveToProjectAction) => action.payload),
      switchMap(({ taskId, projectId, statusId }) =>
        this.taskService.moveToProject(taskId, projectId, statusId).pipe(
          switchMap(task => [
            new MoveToProjectSuccessAction(task),
            // Можно добавить уведомление о успешном перемещении
            // new ShowNotificationAction({ message: 'Задача успешно перемещена', type: 'success' })
          ]),
          catchError(errors => {
            console.error('TaskEffects.moveToProject$ error:', errors);
            return of(new MoveToProjectFailureAction(errors));
          })
        )
      )
    )
  );
}
